var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from "react";
import { getRegisterMessagesByCode } from "./localizationUtils";
import { getCustomMessagesByCode, saveLocalizations, translateText } from "@core/services/services";
import { UXPContext } from "@core/context/Context";
import { hasValue } from "../../utils";
import FormField from "../widget/forms/inputs/FormField";
import Button from "@widget/buttons/Button";
import AsyncButton from "@widget/buttons/AsyncButton";
import classNames from "classnames";
import Tooltip from "../widget/popups/Tooltip";
import useToast from "@notifications/Toast/useToast";
import SkeletonLoader from "@widget/loaders/SkeletonLoader";
import Input from "@widget/forms/inputs/Input";
import Icon from "@icon/Icon";
/**
 * @export
 * This component let's you to configure localisation messages for the enabled languages in iviva
 *
 * @example
 *
 * <LocalizationForm
 * code: 'uxp-core.text.save'
 * />
 */
const LocalizationForm = (props) => {
    const { code, className, useGoogleTranslate } = props;
    const context = useContext(UXPContext);
    const [loading, setLaoding] = useState(true);
    const [languages, setLanguages] = useState([]);
    const [messages, setMessages] = useState([]);
    const [translatingIndex, setTranslatingIndex] = useState(null);
    const [editedCode, setEditedCode] = useState(code || '');
    const toast = useToast();
    useEffect(() => {
        loadEnabledLanguages();
    }, []);
    useEffect(() => {
        // Only fetch messages when languages are loaded
        if (languages.length > 0) {
            getMessages();
        }
    }, [code, languages, props.customMessages]);
    function loadEnabledLanguages() {
        // Get languages from context instead of service call
        const langs = context.enabledLanguages || [];
        const selectedLanguage = langs.find(l => l.Code == context.language);
        const otherLanguages = langs.filter(l => l.Code != context.language);
        // Map to ILanguage format
        const mappedLanguages = [selectedLanguage, ...otherLanguages]
            .filter(Boolean)
            .map(l => ({ name: l.Name, code: l.Code }));
        setLanguages(mappedLanguages);
    }
    function getMessages() {
        return __awaiter(this, void 0, void 0, function* () {
            setLaoding(true);
            // Use custom messages from props if provided, otherwise fetch from backend
            let customMessages = [];
            if (props.customMessages) {
                // Convert custom messages object to array format
                customMessages = Object.entries(props.customMessages).map(([languageCode, message]) => ({
                    LanguageCode: languageCode,
                    Message: message,
                    Key: null
                }));
            }
            else {
                customMessages = yield getCustomMessagesByCode(context, code);
            }
            const registeredMessages = getRegisterMessagesByCode(code);
            console.log('custom_messages ', customMessages);
            const messages = languages.map(l => {
                const _t = { message: '', key: null, hasModified: false, languageCode: l.code, languageName: l.name };
                const _cm = customMessages.find((c) => c.LanguageCode === l.code);
                console.log('custom_messages___', l.code, _cm);
                if (_cm) {
                    _t.message = _cm.Message;
                    _t.key = _cm.Key;
                }
                else {
                    const _rm = registeredMessages[l.code];
                    if (_rm)
                        _t.message = _rm;
                }
                return _t;
            });
            setMessages(messages);
            setLaoding(false);
        });
    }
    // source for auto-translate: the default language row when it has a value,
    // otherwise fall back to the first row that has one
    function findTranslateSource(targetIndex) {
        var _a;
        if (hasValue((_a = messages === null || messages === void 0 ? void 0 : messages[0]) === null || _a === void 0 ? void 0 : _a.message) && targetIndex !== 0)
            return messages[0];
        return messages.find((x, xi) => xi !== targetIndex && hasValue(x.message)) || null;
    }
    function generateTranslation(m, i) {
        return __awaiter(this, void 0, void 0, function* () {
            const fm = findTranslateSource(i);
            if (!fm)
                return;
            setTranslatingIndex(i);
            try {
                const result = yield translateText(context, {
                    text: fm.message,
                    to: m.languageCode,
                    from: fm.languageCode
                });
                if (!result || !hasValue(result.translated)) {
                    toast.error(context.$L('uxp-core.text.tranlate-error'));
                }
                else {
                    toast.success(context.$L('uxp-core.text.tranlate-success'));
                    const prev = structuredClone(messages);
                    prev[i].message = result.translated;
                    prev[i].hasModified = true;
                    setMessages(prev);
                }
            }
            catch (e) {
                console.error('Unable to translate:', e);
                toast.error(context.$L('uxp-core.text.tranlate-error'));
            }
            finally {
                setTranslatingIndex(null);
            }
        });
    }
    function saveMessages() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const saveCode = (props.allowCodeEdit ? editedCode : code).trim();
                if (!saveCode) {
                    toast.error(context.$L('uxp-core.text.message-code-placeholder'));
                    return;
                }
                let result;
                // Use custom onSave if provided, otherwise use default saveLocalizations
                if (props.onSave) {
                    result = yield props.onSave(saveCode, messages);
                }
                else {
                    result = yield saveLocalizations(context, saveCode, messages);
                }
                if (result.success) {
                    // For custom button labels (like "Done"), just close without toast
                    if (props.submitButtonLabel) {
                        if (props.onCancel) {
                            props.onCancel();
                        }
                    }
                    else {
                        toast.success(context.$L('uxp-core.text.changes-saved'));
                        getMessages();
                    }
                }
                else {
                    toast.error(result.error || context.$L('uxp-core.text.save-failed'));
                }
            }
            catch (e) {
                console.error('Save error:', e);
                toast.error(context.$L('uxp-core.text.save-failed'));
            }
        });
    }
    const renderSkeleton = () => (_jsx("div", { className: "uxpcore_localisation_form__skeleton", children: [1, 2, 3].map(i => (_jsxs("div", { className: "uxpcore_localisation_form__skeleton_item", children: [_jsx(SkeletonLoader, { width: "30%", height: "1rem" }), _jsx(SkeletonLoader, { width: "100%", height: "80px" })] }, i))) }));
    return _jsxs("div", { className: classNames("uxpcore_localisation_form", className), children: [loading && renderSkeleton(), !loading && _jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_localisation_form__content", children: [props.allowCodeEdit && (_jsxs(FormField, { children: [_jsx("div", { className: "uxpcore_localisation_form__label", children: _jsx("div", { className: "uxpcore_localisation_form__language_name", children: context.$L('uxp-core.text.message-code') }) }), _jsx(Input, { value: editedCode, onChange: s => setEditedCode(s), placeholder: context.$L('uxp-core.text.message-code-placeholder') })] })), messages.map((m, i) => {
                                const isTranslating = translatingIndex === i;
                                const translateButton = (useGoogleTranslate && findTranslateSource(i)) ? (_jsx(Tooltip, { content: context.$L('uxp-core.text.generate-translation'), position: "top", children: _jsx("div", { className: classNames("uxpcore_localisation_form__translatebutton", {
                                            "uxpcore_localisation_form__translatebutton--translating": isTranslating
                                        }), onClick: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!isTranslating) {
                                                generateTranslation(m, i);
                                            }
                                        }, children: isTranslating ? (_jsx(Icon, { icon: ['fas', 'spinner'], spin: true })) : (_jsx(Icon, { icon: ['fas', 'language'] })) }) })) : null;
                                return _jsxs(FormField, { children: [_jsx("div", { className: "uxpcore_localisation_form__label", children: _jsx("div", { className: "uxpcore_localisation_form__language_name", children: context.$L(m.languageName) }) }), _jsx(Input, { value: m.message, onChange: s => {
                                                console.log('s', s);
                                                const prev = structuredClone(messages);
                                                prev[i].message = s;
                                                prev[i].hasModified = true;
                                                setMessages(prev);
                                            }, suffix: translateButton })] }, i);
                            })] }), _jsxs("div", { className: "uxpcore_localisation_form__footer", children: [!props.hideCancelButton && (_jsx(Button, { title: props.cancelButtonLabel || context.$L('uxp-core.text.cancel'), onClick: () => {
                                    if (props.onCancel) {
                                        props.onCancel();
                                    }
                                    else {
                                        getMessages();
                                    }
                                } })), _jsx(AsyncButton, { title: props.submitButtonLabel || context.$L('uxp-core.text.save-changes'), loadingTitle: context.$L('uxp-core.text.saving-changes'), onClick: saveMessages, className: "uxpcore_localisation_form__save_btn", active: !!messages.find(m => m.hasModified), disabled: !messages.find(m => m.hasModified) })] })] })] });
};
export default LocalizationForm;
